import React, { useState } from 'react';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import { useTranslation } from 'react-i18next';
import { selectColor } from '@panel/commons/core/theme';
import { formatNumber } from '@panel/commons/helpers/formatAmount';
import { Container, ModuleChart, CirclePercentage, CirclePercentageLower, TooltipComponent } from './styles';
import rectangleLarge from '../../assets/img/rectangleConversionL.svg';
import rectangleLargeVA from '../../assets/img/rectangleConversionL_VA.svg';
import rectangleLargeUB from '../../assets/img/rectangleConversionL_UB.svg';
import rectangleLargeCM from '../../assets/img/rectangleConversionL_CM.svg';
import rectangleMedium from '../../assets/img/rectangleConversionM.svg';
import rectangleMediumVA from '../../assets/img/rectangleConversionM_VA.svg';
import rectangleMediumUB from '../../assets/img/rectangleConversionM_UB.svg';
import rectangleMediumCM from '../../assets/img/rectangleConversionM_CM.svg';
import rectangleSmall from '../../assets/img/rectangleConversionS.svg';
import rectangleSmallVA from '../../assets/img/rectangleConversionS_VA.svg';
import rectangleSmallUB from '../../assets/img/rectangleConversionS_UB.svg';
import rectangleSmallCM from '../../assets/img/rectangleConversionS_CM.svg';

const PyramidChart = ({ data = {}, device, isStatisticsByPosting }) => {
  const { t } = useTranslation();
  const siteId = process.env.REACT_APP_BUILD_SITE;
  const { impressionViews = {}, viewsLeads = {}, impressions = {}, visits = {}, leads = {} } = data;
  const [opacityElement, setOpacityElement] = useState(false);

  const tooltipComponent = (chart, totalValue, percentageValue) => {
    return (
      <TooltipComponent>
        {t(`panel.estadisticas.conversionChart.tooltip.${chart}${isStatisticsByPosting ? '.singular' : ''}`, {
          value: totalValue || '0',
          value2: `${percentageValue}%` || '0%'
        })}
      </TooltipComponent>
    );
  };

  const getMeasures = (mMobile, mTablet, mDesktop) => {
    if (device === 'mobile') return mMobile;
    if (device === 'tablet') return mTablet;
    return mDesktop;
  };

  const getRectangleImage = {
    small: () => {
      if (siteId === 'VAMX') return rectangleSmallVA;
      if (siteId === 'CMBR') return rectangleSmallCM;
      if (siteId === 'UBPE') return rectangleSmallUB;
      return rectangleSmall;
    },
    medium: () => {
      if (siteId === 'VAMX') return rectangleMediumVA;
      if (siteId === 'CMBR') return rectangleMediumCM;
      if (siteId === 'UBPE') return rectangleMediumUB;
      return rectangleMedium;
    },
    large: () => {
      if (siteId === 'VAMX') return rectangleLargeVA;
      if (siteId === 'CMBR') return rectangleLargeCM;
      if (siteId === 'UBPE') return rectangleLargeUB;
      return rectangleLarge;
    }
  };

  return (
    <Container device={device}>
      <ModuleChart
        image={getRectangleImage.large}
        width={getMeasures(100, 100, 67)}
        height={getMeasures(52, 57, 77)}
        onMouseEnter={() => {
          if (device === 'desktop') {
            setOpacityElement(true);
          }
        }}
        onMouseLeave={() => {
          setOpacityElement(false);
        }}
        opacityElement={opacityElement ? 0.2 : 1}
        device={device}
        data-panel='panel-estadisticas-conversionChart-module1'
      >
        {device === 'desktop' && (
          <SimpleTooltip
            offset={-60}
            backgroundColor={selectColor('black')}
            isMobile={false}
            text={tooltipComponent('impressions', formatNumber(impressions.total))}
            position='right-center'
            dataPanel='panel-estadisticas-conversionChart-tooltip-impressions'
            sizeArrow='0'
          >
            <div className='textChart' data-panel='panel-estadisticas-conversionChart-textImpressions'>
              {t('panel.estadisticas.generalData.title.impressions')}
            </div>
          </SimpleTooltip>
        )}
        {device !== 'desktop' && (
          <div className='textChart' data-panel='panel-estadisticas-conversionChart-textImpressions-tabletMobile'>
            {t('panel.estadisticas.generalData.title.impressions')}
          </div>
        )}
      </ModuleChart>
      <CirclePercentage
        device={device}
        position={getMeasures(38, 43, 60)}
        data-panel='panel-estadisticas-conversionChart-conversionImpressions'
      >
        {`${formatNumber(impressionViews.conversion)}%` || '0%'}
      </CirclePercentage>
      <ModuleChart
        image={getRectangleImage.medium}
        width={getMeasures(100, 100, 55)}
        height={getMeasures(54, 59, 79)}
        onMouseEnter={() => {
          if (device === 'desktop') {
            setOpacityElement(true);
          }
        }}
        onMouseLeave={() => {
          setOpacityElement(false);
        }}
        opacityElement={opacityElement ? 0.4 : 1}
        device={device}
        data-panel='panel-estadisticas-conversionChart-module2'
      >
        {device === 'desktop' && (
          <SimpleTooltip
            offset={-60}
            backgroundColor={selectColor('black')}
            isMobile={false}
            text={tooltipComponent('visits', formatNumber(visits.total), formatNumber(impressionViews.conversion))}
            position='right-center'
            dataPanel='panel-estadisticas-conversionChart-tooltip-visits'
            sizeArrow='0'
          >
            <div className='textChart' data-panel='panel-estadisticas-conversionChart-textVisits'>
              {t('panel.estadisticas.generalData.title.visits')}
            </div>
          </SimpleTooltip>
        )}
        {device !== 'desktop' && (
          <div className='textChart' data-panel='panel-estadisticas-conversionChart-textVisits-tabletMobile'>
            {t('panel.estadisticas.generalData.title.visits')}
          </div>
        )}
      </ModuleChart>
      <CirclePercentageLower
        device={device}
        position={getMeasures(96, 106, 145)}
        data-panel='panel-estadisticas-conversionChart-conversionVisits'
      >
        {`${formatNumber(viewsLeads.conversion)}%` || '0%'}
      </CirclePercentageLower>
      <ModuleChart
        image={getRectangleImage.small}
        width={getMeasures(70, 100, 37)}
        height={getMeasures(54, 59, 77)}
        onMouseEnter={() => {
          if (device === 'desktop') {
            setOpacityElement(true);
          }
        }}
        onMouseLeave={() => {
          setOpacityElement(false);
        }}
        opacityElement={opacityElement ? 0.5 : 1}
        device={device}
        data-panel='panel-estadisticas-conversionChart-module3'
      >
        {device === 'desktop' && (
          <SimpleTooltip
            offset={-60}
            backgroundColor={selectColor('black')}
            isMobile={false}
            text={tooltipComponent('leads', formatNumber(leads.total), formatNumber(viewsLeads.conversion))}
            position='right-center'
            dataPanel='panel-estadisticas-conversionChart-tooltip-leads'
            sizeArrow='0'
          >
            <div className='textChart' data-panel='panel-estadisticas-conversionChart-textLeads'>
              {t('panel.estadisticas.generalData.title.leads')}
            </div>
          </SimpleTooltip>
        )}
        {device !== 'desktop' && (
          <div className='textChart' data-panel='panel-estadisticas-conversionChart-textLeads-tabletMobile'>
            {t('panel.estadisticas.generalData.title.leads')}
          </div>
        )}
      </ModuleChart>
    </Container>
  );
};

export default PyramidChart;
